import { Descriptions } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { deleteArchive, getArchive } from "../../../api/homepageManagementStudyArchive.api";
import Button from "../../../components/Button";
import ErrorResponse from "../../../components/ErrorResponse";
import SimpleHeader from "../../../components/layout/SimpleHeader";
import Loader from "../../../components/Loader";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import useAuth from "../../../hooks/useAuth";
import useAxios from "../../../hooks/useAxios";
import useNotification from "../../../hooks/useNotification";
import { IGetArchive } from "../../../models/homepageManagementStudyArchive.model";

interface IMatch {
  archiveCode: string;
}

const ArchiveRead = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const match = useRouteMatch<IMatch>();
  const archiveCode = Number(match.params.archiveCode);
  const history = useHistory();
  const location = useLocation();

  const { successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetArchive>();
  const { data: deleteRes, isLoading: deleteIsLoading, error: deleteError, execute: deleteExecute } = useAxios();

  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);

  useEffect(() => {
    if (archiveCode) {
      execute(getArchive(archiveCode));
    }
  }, [archiveCode]);

  useEffect(() => {
    if (deleteRes && !deleteIsLoading) {
      successNotification(t('common.message.delete'));
      history.push(`/homepage/archive${location.search}`);
    }
  }, [deleteRes, deleteIsLoading]);

  const onDelete = () => {
    if (!archiveCode) return;

    deleteExecute(deleteArchive(archiveCode));
  }

  return (
    <div className="content scrollbar">
      {isLoading || deleteIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={deleteError} />
      <SimpleHeader
        iconFile="icon-homepage-big.svg"
        parentMenu={t('common.menu.homepage')}
        currentMenu={t('homepage.archive.title3')}
      />

      <div className='comm-table__area rowType'>
        <Descriptions
          bordered
          column={5}
          size="small"
          labelStyle={{ width: '10%' }}
          contentStyle={{ width: '10%' }}
        >
          <Descriptions.Item label={t('common.column.title')} span={5}>
            {res?.data?.title}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.archive.column4')}>
            {res?.data?.courseName}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.archive.column3')}>
            {res?.data?.subjectName}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.column.writer')}>
            {res?.data?.regMemKorName}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.archive.column8')}>
            {moment(res?.data?.regDate || '').format('YYYY.MM.DD')}
          </Descriptions.Item>
          <Descriptions.Item label={t('homepage.archive.column7')}>
            {res?.data?.readCount}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.column.contents')} span={5}>
            <div dangerouslySetInnerHTML={{ __html: res?.data?.content || '' }} />
          </Descriptions.Item>
          <Descriptions.Item label={t('common.column.file')} span={5}>
            {
              res?.data?.files
                ? res.data.files.map(item => (
                  <a
                    className="download-link mr-1"
                    href={item.fileLocation}
                    download={item.originalName}
                  >
                    {item.originalName}
                  </a>
                ))
                : '-'
            }
          </Descriptions.Item>
        </Descriptions>

        <div className="comm-bx__between">
          <div className="comm-btn__area">
            <Button
              className="btn-basic gray-line"
              text={t('common.button.list')}
              onClick={() => history.push(`/homepage/archive${location.search}`)}
            />
          </div>
          <div className="comm-btn__area right">
            <Button
              className="btn-basic gray"
              text={t('common.button.delete')}
              onClick={() => setConfirmDeleteVisible(true)}
              writeAuthYn={writeAuthYn}
            />
            <Button
              className="btn-basic pink-line"
              text={t('common.button.edit')}
              onClick={() => history.push(`/homepage/archive/${archiveCode}/edit${location.search}`)}
              writeAuthYn={writeAuthYn}
            />
          </div>
        </div>
      </div>

      {confirmDeleteVisible && (
        <ConfirmModal
          visible={confirmDeleteVisible}
          setVisible={setConfirmDeleteVisible}
          onConfirm={onDelete}
          titleText={t('system.menu.title6')}
          messageText={t('system.menu.text3')}
          confirmText={t('common.button.delete')}
          cancelText={t('common.button.cancel')}
        />
      )}
    </div>
  );
}

export default ArchiveRead;
